import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, ShoppingBag } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { NAV } from '@/constants/testIds';

const links = [
  { to: '/assessment', label: 'Assessment', id: NAV.linkAssessment },
  { to: '/consultation', label: 'Consultation', id: NAV.linkConsultation },
  { to: '/consultation/diet', label: 'Diet Plan', id: 'nav-diet' },
  { to: '/products', label: 'Products', id: NAV.linkProducts },
  { to: '/learn', label: 'Learn', id: NAV.linkLearn },
  { to: '/about', label: 'About', id: NAV.linkAbout },
];

const Header = () => {
  const { user, logout } = useAuth();
  const { count: cartCount } = useCart();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled ? 'bg-sans_bone/85 backdrop-blur-xl border-b border-sans_line' : 'bg-transparent'
      }`}
    >
      <div className="sans-container flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid={NAV.logo} className="flex items-baseline gap-2 group">
          <span className="font-serif text-xl md:text-2xl tracking-tight text-sans_ink">Saans Ayurved</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors ${
                  isActive ? 'text-sans_ink' : 'text-sans_mute hover:text-sans_ink'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <Link to="/cart" data-testid={NAV.btnCart} className="relative text-sans_mute hover:text-sans_ink transition-colors" aria-label="Cart">
            <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
            {cartCount > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-sans_ink text-sans_bone text-[10px] font-medium leading-[18px] text-center tracking-normal"
              >
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            )}
          </Link>
          {user?.role === 'admin' && (
            <Link to="/admin" data-testid={NAV.linkAdmin} className="text-[13px] tracking-wide text-sans_ink hover:opacity-70 transition-opacity">Admin</Link>
          )}
          {user?.role === 'doctor' && (
            <Link to="/doctor" data-testid={NAV.linkDoctor} className="text-[13px] tracking-wide text-sans_ink hover:opacity-70 transition-opacity">Doctor</Link>
          )}
          {user ? (
            <>
              <Link to="/dashboard" data-testid={NAV.linkDashboard} className="text-[13px] tracking-wide text-sans_ink hover:opacity-70 transition-opacity flex items-center gap-2">
                <User className="w-4 h-4" strokeWidth={1.5} />
                {user.name?.split(' ')[0] || 'Account'}
              </Link>
              <button
                data-testid={NAV.btnLogout}
                onClick={async () => { await logout(); nav('/'); }}
                className="text-[13px] tracking-wide text-sans_mute hover:text-sans_ink transition-colors"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link to="/login" data-testid={NAV.linkLogin} className="text-[13px] tracking-wide text-sans_ink hover:opacity-70 transition-opacity">
              Sign in
            </Link>
          )}
        </div>

        <button
          data-testid={NAV.mobileMenuToggle}
          className="lg:hidden text-sans_ink"
          onClick={() => setOpen((s) => !s)}
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-sans_bone border-t border-sans_line animate-fade-in">
          <div className="sans-container py-8 flex flex-col gap-6">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={l.id + '-mobile'} className="text-lg text-sans_ink">{l.label}</NavLink>
            ))}
            <div className="sans-divider" />
            <Link to="/cart" className="text-sans_ink">Cart{cartCount > 0 ? ` (${cartCount})` : ''}</Link>
            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link to="/admin" className="text-lg text-sans_ink font-medium">Admin dashboard</Link>
                )}
                {user.role === 'doctor' && (
                  <Link to="/doctor" className="text-lg text-sans_ink font-medium">Doctor dashboard</Link>
                )}
                <Link to="/dashboard" className="text-sans_ink">My account</Link>
                <button className="text-left text-sans_mute" onClick={async () => { await logout(); nav('/'); }}>Sign out</button>
              </>
            ) : (
              <Link to="/login" className="text-sans_ink">Sign in</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
